import {
	ChartType,
	Granularity,
	type ChartCardConfig,
	type DimensionConfig,
} from '../../../../models/chart-config.model';
import { CardType } from '../../../../models/card-model';
import type { ChartDataResponse } from '../../../../models/card-model';
import type { ChartData } from '../../../cards/jepto-chart-card/jepto-chart-card.component';
import {
	resolveTableColumnHeaderLabel,
	type TableLabelSchemaContext,
} from '../../../../utils/table-column-display.util';
import {
	isScatterChartType,
	isTimeSeriesChartType,
	usesCategoryAxisChartType,
} from '../../shared/data-tab/chart-data-tab-axis.util';
import type { DataService } from '../../../../services/data.service';
import { CHART_TYPE_REQUIREMENTS } from '../../../../services/chart-config-change.types';
import { usesCategoryBreakdownChartType } from '../../../../utils/category-breakdown-visualization.util';

/** True when at least one series row includes plotted points. */
export function chartSeriesHasPlottablePoints(series: unknown): boolean {
	if (!Array.isArray(series)) {
		return false;
	}
	return series.some(item => {
		if (!item || typeof item !== 'object') {
			return false;
		}
		const points = (item as { data?: unknown[] }).data;
		return Array.isArray(points) && points.length > 0;
	});
}

function chartTypeRequiresPlottableSeries(
	chartType: ChartType | string | undefined
): boolean {
	if (!chartType) {
		return true;
	}
	const type = String(chartType);
	return (
		isScatterChartType(type) ||
		isTimeSeriesChartType(type) ||
		usesCategoryAxisChartType(type)
	);
}

/** Whether chart preview can render from this payload. */
export function hasRenderableChartPayload(
	data: ChartData | ChartDataResponse,
	fallbackChartType?: ChartType | string
): boolean {
	if (!data?.data) return false;
	const inner = data.data as Record<string, unknown>;
	const chartType =
		(data as ChartDataResponse).chartType ?? fallbackChartType;
	if (chartType === 'donut' || chartType === ChartType.Donut) {
		const donut = inner['donutSeries'];
		return Array.isArray(donut) && donut.length > 0;
	}
	if (chartType === ChartType.Number || chartType === 'number') {
		const v = inner['value'];
		return v !== undefined && v !== null;
	}
	const series = inner['series'];
	if (!Array.isArray(series) || series.length === 0) {
		return false;
	}
	if (chartTypeRequiresPlottableSeries(chartType)) {
		return chartSeriesHasPlottablePoints(series);
	}
	return true;
}

/**
 * Completed empty query: payload has the chart shape but nothing to plot.
 * Distinct from a missing payload (still in flight).
 */
export function isSettledEmptyChartPayload(
	data: ChartData | ChartDataResponse | null | undefined,
	fallbackChartType?: ChartType | string
): boolean {
	if (!data?.data || hasRenderableChartPayload(data, fallbackChartType)) {
		return false;
	}
	const inner = data.data as Record<string, unknown>;
	const chartType =
		(data as ChartDataResponse).chartType ?? fallbackChartType;
	if (chartType === 'donut' || chartType === ChartType.Donut) {
		return Array.isArray(inner['donutSeries']);
	}
	if (chartType === ChartType.Number || chartType === 'number') {
		return Object.prototype.hasOwnProperty.call(inner, 'value');
	}
	return Array.isArray(inner['series']);
}

/** Persist plottable series or a completed empty result into DataService. */
export function shouldPersistChartRuntimePayload(
	data: ChartData | ChartDataResponse | null | undefined,
	fallbackChartType?: ChartType | string
): boolean {
	if (!data) {
		return false;
	}
	return (
		hasRenderableChartPayload(data, fallbackChartType) ||
		isSettledEmptyChartPayload(data, fallbackChartType)
	);
}

/** Normalize and store chart payloads from AppSync WebSocket (editor + report grid). */
export function applyChartWebSocketDataToDataService(
	dataService: DataService,
	event: {
		cardId: string;
		chartType?: ChartType | string;
		data?: unknown;
		requestId?: string;
	}
): boolean {
	if (!event.cardId?.trim() || event.data == null) {
		return false;
	}
	const payload = {
		cardId: event.cardId,
		chartType: event.chartType,
		data: event.data,
		requestId: event.requestId,
	} as ChartDataResponse;
	if (!shouldPersistChartRuntimePayload(payload, event.chartType)) {
		return false;
	}
	dataService.updateCardData(payload);
	return true;
}

/** True when `data.metrics` includes enough named fields for `getCardData`. */
export function chartCardHasPreviewQueryFields(
	config: ChartCardConfig | null | undefined
): boolean {
	const chartType = config?.chartType;
	if (!chartType) {
		return false;
	}
	const requirements = CHART_TYPE_REQUIREMENTS[chartType];
	if (!requirements) {
		return false;
	}
	const configuredMetrics = (config?.data?.metrics ?? []).filter(
		m => typeof m?.name === 'string' && m.name.trim().length > 0
	).length;
	return configuredMetrics >= requirements.minMetrics;
}

/** Detect WebSocket-delivered updates while waiting after `getCardData === true`. */
export function chartDataPayloadFingerprint(
	data: ChartDataResponse | undefined
): string {
	if (!data) return '';
	const parts: string[] = [data.requestId ?? ''];
	const inner = data.data as Record<string, unknown> | undefined;
	if (!inner) return parts.join('|');
	if (Array.isArray(inner['series'])) {
		const series = inner['series'] as Array<{
			data?: unknown[];
			comparisonData?: unknown[];
			aggregationValue?: number;
		}>;
		parts.push(`s${series.length}`);
		series.forEach((s, index) => {
			const points = s.data ?? [];
			parts.push(`l${index}:${points.length}`);
			parts.push(`a${index}:${s.aggregationValue ?? ''}`);
			parts.push(`c${index}:${s.comparisonData?.length ?? 0}`);
			parts.push(JSON.stringify(points[0] ?? null));
			parts.push(JSON.stringify(points[points.length - 1] ?? null));
		});
	} else if (Array.isArray(inner['donutSeries'])) {
		parts.push(`d${(inner['donutSeries'] as unknown[]).length}`);
	} else if (inner['value'] !== undefined && inner['value'] !== null) {
		parts.push(`n${String(inner['value'])}`);
		const comparison = inner['comparisonData'] as
			| { value?: number; unit?: string }
			| undefined;
		if (comparison && typeof comparison === 'object') {
			parts.push(`nc:${comparison.value ?? ''}:${comparison.unit ?? ''}`);
		}
	}
	return parts.join('|');
}

/** True when cached chart series dates fall within the fetch key date range. */
export function chartCachedPreviewMatchesFetchKeyDateRange(
	cached: ChartDataResponse,
	timeDimensionInput: Record<string, unknown>
): boolean {
	const dateRange = timeDimensionInput['dateRange'] as string[] | undefined;
	if (!dateRange?.[0] || !dateRange?.[1]) {
		return true;
	}
	const [wantStart, wantEnd] = dateRange;
	const inner = cached.data as Record<string, unknown> | undefined;
	const series = inner?.['series'] as
		| Array<{ data?: Array<[unknown, unknown]> }>
		| undefined;
	if (!series?.length) {
		return true;
	}
	const dates: string[] = [];
	for (const s of series) {
		for (const pt of s.data ?? []) {
			const x = pt?.[0];
			if (typeof x === 'string' && /^\d{4}-\d{2}-\d{2}/.test(x)) {
				dates.push(x);
			}
		}
	}
	// Scatter and other non-time-series charts have no ISO date points to compare.
	if (!dates.length) {
		return true;
	}
	dates.sort();
	return dates[0] >= wantStart && dates[dates.length - 1] <= wantEnd;
}

export function extractRawCardDataFromGql(response: unknown): unknown {
	return (response as { data?: { getItem?: { cardData?: unknown } } }).data
		?.getItem?.cardData;
}

function normalizeChartDataPayload(
	raw: unknown,
	cardConfig: ChartCardConfig
): Partial<ChartDataResponse> | null {
	if (!raw || typeof raw !== 'object') return null;
	const obj = raw as Record<string, unknown>;
	const hasNestedData =
		'data' in obj &&
		typeof obj.data === 'object' &&
		obj.data !== null &&
		!Array.isArray(obj.data);
	const topLevelData =
		'series' in obj ||
		'donutSeries' in obj ||
		'rowData' in obj ||
		'value' in obj;
	if (hasNestedData) return obj as Partial<ChartDataResponse>;
	if (topLevelData) {
		return {
			cardId: cardConfig.cardId,
			cardType: CardType.Chart,
			chartType: cardConfig.chartType,
			data: obj as ChartDataResponse['data'],
		};
	}
	return null;
}

export function normalizeCardDataResponse(
	response: unknown,
	cardConfig: ChartCardConfig
): Partial<ChartDataResponse> | null {
	const rawData = extractRawCardDataFromGql(response);
	if (typeof rawData === 'boolean') return null;
	let parsedData: unknown;
	if (typeof rawData === 'string') {
		try {
			parsedData = JSON.parse(rawData) as unknown;
		} catch {
			return null;
		}
	} else {
		parsedData = rawData;
	}
	if (!parsedData || typeof parsedData !== 'object') return null;
	const payload = normalizeChartDataPayload(parsedData, cardConfig);
	if (!payload) return null;
	return {
		...payload,
		cardId: cardConfig.cardId,
		cardType: CardType.Chart,
		chartType: payload.chartType ?? cardConfig.chartType ?? ChartType.Line,
	};
}

/** Granularity → header label for the time-series first column. */
function chartPreviewGranularityLabel(
	granularity: unknown
): string | undefined {
	if (typeof granularity !== 'string') return undefined;
	switch (granularity.trim().toUpperCase()) {
		case Granularity.Day:
			return 'Date';
		case Granularity.Week:
			return 'Week';
		case Granularity.Month:
			return 'Month';
		case Granularity.Quarter:
			return 'Quarter';
		case Granularity.Year:
			return 'Year';
		case Granularity.DayOfWeek:
			return 'Day of week';
		default:
			return undefined;
	}
}

function chartPreviewGroupingDimensionLabel(
	dimensions: DimensionConfig[] | undefined,
	schema?: TableLabelSchemaContext
): string {
	if (!Array.isArray(dimensions) || dimensions.length === 0) {
		return '';
	}
	const firstDim = dimensions[0];
	const name = firstDim?.name?.trim();
	if (name) {
		return resolveTableColumnHeaderLabel({
			columnId: firstDim.id ?? name,
			fieldName: name,
			cardLabel: firstDim.label,
			headerName: firstDim.label,
			schema,
		});
	}
	const label = firstDim?.label?.trim();
	if (label) {
		return resolveTableColumnHeaderLabel({
			columnId: firstDim.id ?? label,
			headerName: label,
			cardLabel: label,
			schema,
		});
	}
	return '';
}

export function chartPreviewDimensionLabel(
	config: ChartCardConfig,
	schema?: TableLabelSchemaContext
): string {
	const data = config.data;
	if (!data) return '';

	const isScatter = isScatterChartType(String(config.chartType ?? ''));
	if (isScatter) {
		const groupingLabel = chartPreviewGroupingDimensionLabel(
			data.dimensions,
			schema
		);
		if (groupingLabel) {
			return groupingLabel;
		}
	}

	const td = data.timeDimension;
	if (td) {
		const granularityLabel = chartPreviewGranularityLabel(td.granularity);
		if (granularityLabel) return granularityLabel;
		if (td.dimension) {
			return resolveTableColumnHeaderLabel({
				columnId: td.dimension,
				fieldName: td.dimension,
				schema,
			});
		}
		return 'Date';
	}

	return chartPreviewGroupingDimensionLabel(data.dimensions, schema);
}

/**
 * Breakdown column header for Data Preview (line/area `dimensions[0]`,
 * bar/column `dimensions[1]`).
 */
export function chartPreviewBreakdownLabel(
	config: ChartCardConfig,
	schema?: TableLabelSchemaContext
): string {
	const data = config.data;
	if (!data?.dimensions?.length) {
		return '';
	}
	const chartType = String(config.chartType ?? '');
	if (isTimeSeriesChartType(chartType)) {
		return chartPreviewGroupingDimensionLabel(data.dimensions, schema);
	}
	if (
		usesCategoryBreakdownChartType(chartType) &&
		data.dimensions.length >= 2
	) {
		return chartPreviewGroupingDimensionLabel([data.dimensions[1]], schema);
	}
	return '';
}

/** Primary metric column header for Data Preview long-format breakdown tables. */
export function chartPreviewMetricLabel(
	config: ChartCardConfig,
	schema?: TableLabelSchemaContext
): string {
	const metric = config.data?.metrics?.[0];
	if (!metric) {
		return '';
	}
	const name = metric.name?.trim();
	if (name) {
		return resolveTableColumnHeaderLabel({
			columnId: metric.id ?? name,
			fieldName: name,
			cardLabel: metric.label,
			headerName: metric.label,
			schema,
		});
	}
	const label = metric.label?.trim();
	if (label) {
		return resolveTableColumnHeaderLabel({
			columnId: metric.id ?? label,
			headerName: label,
			cardLabel: label,
			schema,
		});
	}
	return '';
}
